import React, { useEffect, useState } from "react";
import { handleGetRequest } from "../../services/GetTemplate";
import styles from "./InvalidInvoices.module.scss";
import { FiAlertTriangle, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const InvalidInvoicesList = ({ startDate, endDate }) => {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    limit: 20,
    skip: 0,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    if (startDate && endDate) {
      fetchInvalidInvoices();
    }
  }, [startDate, endDate, pagination.skip, pagination.limit]);

  const fetchInvalidInvoices = async () => {
    try {
      setLoading(true);
      const response = await handleGetRequest(
        `/analytics/financial/invalid-paid-invoices-by-date?` +
        `startDate=${startDate}&endDate=${endDate}&` +
        `limit=${pagination.limit}&skip=${pagination.skip}`
      );
      
      setInvoices(response?.data?.invoices || []);
      setSummary(response?.data?.summary);
      setPagination({
        ...pagination,
        total: response?.data?.total || 0,
        pages: response?.data?.pages || 0
      });
    } catch (error) {
      console.error("Fetch invalid invoices error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousPage = () => {
    if (pagination.skip > 0) {
      setPagination({
        ...pagination,
        skip: pagination.skip - pagination.limit
      });
    }
  };

  const handleNextPage = () => {
    if (pagination.skip + pagination.limit < pagination.total) {
      setPagination({
        ...pagination,
        skip: pagination.skip + pagination.limit
      });
    }
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  if (!startDate || !endDate) {
    return null;
  }

  return (
    <div className={styles.invalidInvoicesList}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <FiAlertTriangle className={styles.icon} />
          <h3>⚠️ Invalid Paid Invoices (Excluded from calculations)</h3>
        </div>
        <p className={styles.subtitle}>
          Invoices with paymentStatus "paid" but paidAmount is 0
        </p>
      </div>

      {/* Summary Info */}
      {summary && summary.count > 0 && (
        <div className={styles.summaryBanner}>
          <div className={styles.summaryItem}>
            <span className={styles.label}>Total Invalid Invoices:</span>
            <span className={styles.value}>{summary.count}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.label}>Total Invoice Amount:</span>
            <span className={styles.value}>
              ${summary.totalInvoiceAmount?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.label}>Average Amount:</span>
            <span className={styles.value}>
              ${summary.avgAmount?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}

      {/* Table */}
      {invoices.length > 0 ? (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.analyticsTable}>
              <thead>
                <tr>
                  <th>Invoice No</th>
                  <th>Client Name</th>
                  <th>Invoice Amount</th>
                  <th>Paid Amount</th>
                  <th>Review Status</th>
                  <th>Created Date</th>
                  <th>Days Old</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice, idx) => {
                  const daysOld = Math.floor(
                    (new Date() - new Date(invoice.createdAt)) / (1000 * 60 * 60 * 24)
                  );
                  
                  return (
                    <tr key={invoice._id} className={styles.warningRow}>
                      <td className={styles.invoiceNo}>{invoice.invoiceNo || 'N/A'}</td>
                      <td className={styles.clientName}>
                        {invoice.clientName || 
                          (invoice.clientId?.firstName && invoice.clientId?.lastName
                            ? `${invoice.clientId.firstName} ${invoice.clientId.lastName}`
                            : 'Unknown')}
                      </td>
                      <td className={styles.amount}>
                        ${(invoice.invoiceAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td className={styles.amountZero}>
                        ${(invoice.paidAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td>
                        <span className={`${styles.badge} ${styles[invoice.reviewStatus?.toLowerCase()]}`}>
                          {invoice.reviewStatus || 'Unknown'}
                        </span>
                      </td>
                      <td className={styles.date}>
                        {new Date(invoice.createdAt).toLocaleDateString()}
                      </td>
                      <td className={styles.daysOld}>
                        <span className={daysOld > 30 ? styles.overdue : ''}>
                          {daysOld}d
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className={styles.pagination}>
              <button 
                onClick={handlePreviousPage} 
                disabled={pagination.skip === 0}
                className={styles.paginationBtn}
              >
                <FiChevronLeft /> Previous
              </button>
              <span className={styles.pageInfo}>
                Page {currentPage} of {pagination.pages}
              </span>
              <button 
                onClick={handleNextPage} 
                disabled={pagination.skip + pagination.limit >= pagination.total}
                className={styles.paginationBtn}
              >
                Next <FiChevronRight />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className={styles.emptyState}>
          <p>✅ No invalid invoices for this timeframe</p>
        </div>
      )}
    </div>
  );
};

export default InvalidInvoicesList;